import React from 'react';
import clsx from 'clsx';
import { Radio, type RadioChangeEvent } from 'antd';
import { Gap } from '@common/components/gap';
import { CardCollapse } from '@common/components/card-collapse';
import { Colors } from '@common/tokens';

export type ComboRadioOption = {
  label: string;
  value?: string;
  disabled?: boolean;
  open?: boolean;
  content?: React.ReactNode;
};

type Props = {
  options?: ComboRadioOption[];
  disabled?: boolean;
  name?: string;
  value?: ComboRadioOption['value'];
  onChange?: (e: RadioChangeEvent) => void;
};

export const ComboRadio = ({
  options = [],
  name,
  value,
  onChange,
  disabled,
}: Props) => {
  // NOTE: two ComboRadios on a page often share option values, so ids are
  // namespaced per instance to keep one label from toggling the other's radio.
  const instanceId = React.useId();
  const domId = (optionValue?: string) => `${instanceId}-${optionValue}`;

  return (
    <>
      <Radio.Group
        name={name}
        value={value}
        disabled={disabled}
        onChange={onChange}
        className="wk-combo-radio"
      >
        <Gap vertical fullWidth size="md">
          {options.map((option) => (
            <label
              key={option.value}
              htmlFor={domId(option.value)}
              className="wk-combo-radio__label"
            >
              <CardCollapse
                className={clsx('wk-combo-radio__item', {
                  'wk-combo-radio__item--selected': value === option.value,
                })}
                key={option.value}
                open={option.open || value === option.value}
                label={
                  <Radio
                    id={domId(option.value)}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </Radio>
                }
              >
                {option.content}
              </CardCollapse>
            </label>
          ))}
        </Gap>
      </Radio.Group>
      <style jsx>{`
        :global(.wk-combo-radio) {
          display: inline;
          width: 100%;
        }
        :global(.wk-combo-radio .wk-combo-radio__label) {
          display: block;
          width: 100%;
        }
        :global(.wk-combo-radio .wk-combo-radio__label:hover) {
          cursor: pointer;
        }
        :global(.wk-combo-radio .wk-card-collapse) {
          width: 100%;
        }
        :global(.wk-combo-radio .wk-card-collapse .ant-card-body) {
          padding: 24px;
        }
        :global(.wk-combo-radio .wk-combo-radio__item--selected) {
          border-color: ${Colors.elm};
        }
      `}</style>
    </>
  );
};
